import { useEffect, useState } from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AdditionalContactField from "../../../../../../components/additional-contact/additional-contact";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientDetailsRequest,
  getTenantAllUsersRequest,
} from "../../../../../../redux/clients/slice";
import { updateClientSFSContactsRequest } from "../../../../../../utils/APICalls";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

export default function EditSFSContactDetails({
  showDialog,
  closeDialog,
  data,
}: props) {
  const [selectedSalesManager, setSelectedSalesManager] = useState("contact0");
  const [selectedRelationshipManager, setSelectedRelationshipManager] =
    useState("contact0");
  const [salesManagers, setSalesManagers] = useState<any[]>([]);
  const [isSalesManagerEmptyError, setIsSalesManagerEmptyError] =
    useState(false);
  const [relationshipManagers, setRelationshipManagers] = useState<any[]>([]);
  const [isRelationshipManagerEmptyError, setIsRelationshipManagerEmptyError] =
    useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  const { loading } = useSelector((state: any) => state.clients);
  const { tenantAllUsers } = useSelector((state: any) => state.clients);

  useEffect(() => {
    dispatch(getTenantAllUsersRequest());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      const allSalesManagers = [];
      const primarySalesManager = data?.salesManagers?.filter(
        (e: any) => e.isPrimary
      );
      if (primarySalesManager.length > 0) {
        allSalesManagers.push(primarySalesManager[0]);
      }
      const nonPrimarySalesManager = data?.salesManagers?.filter(
        (e: any) => e.isPrimary === false
      );
      if (nonPrimarySalesManager.length > 0) {
        allSalesManagers.push(...nonPrimarySalesManager);
      }
      if (allSalesManagers.length === 0) {
        //if there is no user then add a user field with primary
        allSalesManagers.push({ userId: "", isPrimary: true });
      }
      setSalesManagers(allSalesManagers);

      const allRelationshipManager = [];
      const primaryRelationshipManager = data?.relationShipManagers?.filter(
        (e: any) => e.isPrimary
      );
      if (primaryRelationshipManager.length > 0) {
        allRelationshipManager.push(primaryRelationshipManager[0]);
      }
      const nonPrimaryRelationshipManager = data?.relationShipManagers?.filter(
        (e: any) => e.isPrimary === false
      );
      if (nonPrimaryRelationshipManager.length > 0) {
        allRelationshipManager.push(...nonPrimaryRelationshipManager);
      }
      if (allRelationshipManager.length === 0) {
        //if there is no user then add a user field with primary
        allRelationshipManager.push({ userId: "", isPrimary: true });
      }
      setRelationshipManagers(allRelationshipManager);
    }
  }, [data]);

  const handleSelectedSalesManagerChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedSalesManager(event.target.value);
    const allSalesManagers = JSON.parse(JSON.stringify(salesManagers));
    if (selectedIndex >= 0) {
      // if radio selected set to primary
      allSalesManagers.forEach((allSalesManager: any, index: number) => {
        if (index === selectedIndex) {
          allSalesManager.isPrimary = true;
        } else {
          allSalesManager.isPrimary = false;
        }
      });
    }
    setSalesManagers(allSalesManagers);
  };

  const onSalesManagersChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allSalesManagers = JSON.parse(JSON.stringify(salesManagers));
    allSalesManagers[index].userId = e.target.value;
    setSalesManagers(allSalesManagers);
  };

  const addSalesManagersClick = () => {
    setSalesManagers((prev) => [...prev, { userId: "", isPrimary: false }]);
  };

  const handleSalesManagerDelete = (index: number) => {
    const allSalesManagers = [...salesManagers];
    allSalesManagers.splice(index, 1);
    setSalesManagers(allSalesManagers);

    //if delete user is above selected user then reset user by -1
    const selectedIndex = Number(
      selectedSalesManager.substring(selectedSalesManager.length - 1)
    );
    if (selectedIndex > index)
      setSelectedSalesManager(`contact${selectedIndex - 1}`);
  };

  const handleSelectedRelationshipManagerChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedRelationshipManager(event.target.value);
    const allRelationshipManagers = JSON.parse(
      JSON.stringify(relationshipManagers)
    );
    if (selectedIndex >= 0) {
      // if radio selected set to primary
      allRelationshipManagers.forEach(
        (allRelationshipManager: any, index: number) => {
          if (index === selectedIndex) {
            allRelationshipManager.isPrimary = true;
          } else {
            allRelationshipManager.isPrimary = false;
          }
        }
      );
    }
    setRelationshipManagers(allRelationshipManagers);
  };

  const onRelationshipManagersChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allRelationshipManagers = JSON.parse(
      JSON.stringify(relationshipManagers)
    );
    allRelationshipManagers[index].userId = e.target.value;
    setRelationshipManagers(allRelationshipManagers);
  };

  const addRelationshipManagersClick = () => {
    setRelationshipManagers((prev) => [
      ...prev,
      { userId: "", isPrimary: false },
    ]);
  };

  const handleRelationshipManagerDelete = (index: number) => {
    const allRelationshipManagers = [...relationshipManagers];
    allRelationshipManagers.splice(index, 1);
    setRelationshipManagers(allRelationshipManagers);

    //if delete user is above selected user then reset user by -1
    const selectedIndex = Number(
      selectedRelationshipManager.substring(
        selectedRelationshipManager.length - 1
      )
    );
    if (selectedIndex > index)
      setSelectedRelationshipManager(`contact${selectedIndex - 1}`);
  };

  const onSubmitClick = () => {
    if (isSalesManagerEmpty()) {
      setIsSalesManagerEmptyError(true);
      return;
    }
    if (isSalesManagerDuplicate()) {
      return;
    }

    if (isRelatioshipManagerEmpty()) {
      setIsRelationshipManagerEmptyError(true);
      return;
    }
    if (isRelatioshipManagerDuplicate()) {
      return;
    }

    const allSalesManagers: any[] = [];
    salesManagers.forEach((salesManager) => {
      const manager = {
        userId: salesManager.userId,
        isPrimary: salesManager.isPrimary,
      };
      allSalesManagers.push(manager);
    });

    const allRelationshipManagers: any[] = [];
    relationshipManagers.forEach((relationshipManager) => {
      const manager = {
        userId: relationshipManager.userId,
        isPrimary: relationshipManager.isPrimary,
      };
      allRelationshipManagers.push(manager);
    });

    const payload = {
      id: data.id,
      salesManagers: allSalesManagers,
      relationshipManagers: allRelationshipManagers,
    };
    updateClientSFSContacts(payload);
  };

  const isSalesManagerEmpty = () => {
    let isEmpty = false;
    salesManagers.forEach((salesManager) => {
      if (!salesManager.userId) isEmpty = true;
    });
    return isEmpty;
  };

  const isSalesManagerDuplicate = () => {
    let isDuplicate = false;
    const valueArr = salesManagers.map((item) => item.userId);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const isRelatioshipManagerEmpty = () => {
    let isEmpty = false;
    relationshipManagers.forEach((relationshipManager) => {
      if (!relationshipManager.userId) isEmpty = true;
    });
    return isEmpty;
  };

  const isRelatioshipManagerDuplicate = () => {
    let isDuplicate = false;
    const valueArr = relationshipManagers.map((item) => item.userId);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const updateClientSFSContacts = async (payload: any) => {
    setShowLoading(true);
    const response = await updateClientSFSContactsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getClientDetailsRequest({ id: data?.id }));
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Edit Client - SFS Contact Details"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={onSubmitClick}
    >
      <Box>
        <Box className="user-name-layout">
          <Typography className="label-txt">Sales Manager</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography>Primary</Typography>
            {salesManagers.map((item, index) => (
              <AdditionalContactField
                index={index}
                key={index}
                selectedValue={selectedSalesManager}
                onRadioChange={(e: any) =>
                  handleSelectedSalesManagerChange(e, index)
                }
                value={item.userId}
                onValueChange={(e: any) => onSalesManagersChange(e, index)}
                onDelete={() => handleSalesManagerDelete(index)}
                allUsers={tenantAllUsers}
                selectedUsers={salesManagers}
                isUserEmptyError={isSalesManagerEmptyError}
              />
            ))}
            {salesManagers?.length < 4 && (
              <Button
                sx={{ mt: 2, fontWeight: "510" }}
                variant="text"
                startIcon={<AddOutlinedIcon />}
                onClick={addSalesManagersClick}
              >
                Add Additional
              </Button>
            )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Relationship Manager</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography>Primary</Typography>
            {relationshipManagers.map((item, index) => (
              <AdditionalContactField
                index={index}
                key={index}
                selectedValue={selectedRelationshipManager}
                onRadioChange={(e: any) =>
                  handleSelectedRelationshipManagerChange(e, index)
                }
                value={item.userId}
                onValueChange={(e: any) =>
                  onRelationshipManagersChange(e, index)
                }
                onDelete={() => handleRelationshipManagerDelete(index)}
                allUsers={tenantAllUsers}
                selectedUsers={relationshipManagers}
                isUserEmptyError={isRelationshipManagerEmptyError}
              />
            ))}
            {relationshipManagers?.length < 4 && (
              <Button
                sx={{ mt: 2, fontWeight: "510" }}
                variant="text"
                startIcon={<AddOutlinedIcon />}
                onClick={addRelationshipManagersClick}
              >
                Add Additional
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {(loading || showLoading) && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
